import { Image as ImageIcon } from '@mui/icons-material';
import { Box, CircularProgress, Fade } from '@mui/material';
import type { ImgHTMLAttributes } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ProgressImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'width' | 'height' | 'src'> {
  src?: string | null;
  width: number | string;
  height?: number | string;
  radius?: number | string;
  objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down';
  aspectRatio?: string | number;
}

export function ProgressiveImage({
  src,
  alt,
  width,
  height,
  radius = '4px',
  objectFit = 'contain',
  aspectRatio = 'auto',
  style,
  loading = 'lazy',
  ...imgProps
}: ProgressImageProps) {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const showPlaceholderIcon = !src || error;
  const isLoading = !!src && !loaded && !error;
  const effectiveAspectRatio = !src ? '16/9' : aspectRatio;

  // Handle src changes with cleanup
  useEffect(() => {
    if (!src) {
      setLoaded(false);
      setError(false);
      return;
    }

    // Reset states
    setLoaded(false);
    setError(false);

    // Preload to handle race conditions
    const img = new Image();
    let cancelled = false;

    img.onload = () => {
      if (!cancelled) setLoaded(true);
    };

    img.onerror = () => {
      if (!cancelled) setError(true);
    };

    img.src = src;

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  const containerSx = {
    position: 'relative',
    width,
    borderRadius: radius,
    overflow: 'hidden',
    backgroundColor: 'background.default',
    ...(height
      ? { height }
      : {
          display: 'block',
          aspectRatio: effectiveAspectRatio,
        }),
  };

  return (
    <Box sx={containerSx}>
      {/* Placeholder/Error state */}
      {showPlaceholderIcon && (
        <Box
          role="img"
          aria-label={alt || 'Image not available'}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ImageIcon
            sx={{
              fontSize: 48,
              color: 'text.disabled',
              opacity: 0.5,
            }}
          />
        </Box>
      )}

      {/* Loading spinner */}
      {isLoading && (
        <Box
          role="status"
          aria-live="polite"
          aria-label={t('common.status.loading_image')}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <CircularProgress aria-label={t('common.status.loading_image')} size={40} />
        </Box>
      )}

      {/* Image - always rendered but hidden until loaded */}
      {src && !error && (
        <Fade in={loaded} timeout={180}>
          <Box
            component="img"
            src={src}
            alt={alt}
            loading={loading}
            sx={{
              width: '100%',
              height: height ? '100%' : 'auto',
              objectFit: height ? objectFit : undefined,
              aspectRatio: !height ? effectiveAspectRatio : undefined,
              display: 'block',
              position: 'relative',
            }}
            style={style}
            {...imgProps}
          />
        </Fade>
      )}
    </Box>
  );
}
